'use client';

import { FactiiiRequirement, type FactiiiTag } from '@shared/all/domains/factiiis';
import { type Post } from '@shared/all/domains/posts';
import { type Upload } from '@shared/all/domains/uploads';
import { calculateEditTronCostBreakdown } from '@shared/utils/helpers/formatNumbers';
import toast from '@shared/utils/helpers/toast';
import { getDimensionsFromUri, uriToBase64 } from '@shared/utils/helpers/uploadHelper';
import useDatabase from '@shared/utils/hooks/useDatabase';
import { useFactiiiHandlers } from '@shared/utils/hooks/useFactiiiHandlers';
import usePreference from '@shared/utils/hooks/usePreference';
import useUpload from '@shared/utils/hooks/useUpload';
import useUser from '@shared/utils/hooks/useUser';
import { ChevronDown, ChevronUp } from 'lucide-react';
import Link from 'next/link';
import { useState } from 'react';
import superjson from 'superjson';
import { useTronAffordability } from '../../hooks/useTronAffordability';
import { client } from '../../utils/trpc';
import Button from '../common/Button';
import Modal, { ModalContent, ModalFooter, ModalHeader } from '../HOC/Modal';
import { DefaultFactiiiButtons } from '../posts/NewPost/DefaultFactiiiButtons';
import PostItemBody from '../posts/PostItemBody';
import PostItemHead from '../posts/PostItemHead';
import { NewPostFactiii } from './NewPostFactiii';
import { type SelectedAward } from './SelectedFactiiis';
type Props = {
  closeHandler: () => void;
  post: Post;
  refreshPost: (id: string) => void;
  nsfw: boolean;
  setNsfw: (nsfw: boolean) => void;
  politics: boolean;
  setPolitics: (politics: boolean) => void;
  troll: boolean;
  setTroll: (troll: boolean) => void;
};
const FactiiiModal = (props: Props) => {
  const {
    checkRequirementsMet,
    remappedAddFactiiis
  } = useFactiiiHandlers();
  const {
    currentUser
  } = useUser();
  const {
    preferenceState
  } = usePreference(client);
  const {
    processPrivateFactiiis,
    uploadFromBase64
  } = useDatabase();
  const {
    uploadOne
  } = useUpload(client);
  const queryKey = ['TagPost', props.post.id, undefined];
  const [buttonLoading, setButtonLoading] = useState(false);
  const [showPostBody, setShowPostBody] = useState(false);
  const [myPostFactiiiCounts, setMyPostFactiiiCounts] = useState<FactiiiTag[]>([]);
  // Server-derived baseline; data === initialData ⇒ nothing to tag/reset.
  const [initialData, setInitialData] = useState<FactiiiTag[]>([]);
  // Awards staged for giving on Tag — each carries its own anonymous + count.
  const [selectedAwards, setSelectedAwards] = useState<SelectedAward[]>([]);
  const submitHandler = async (postFactiiiCounts: FactiiiTag[]) => {
    setButtonLoading(true);
    if (props.post.isPrivate) {
      // Upload factiii photos to private storage
      const factiiiUploads: {
        factiiiId: number;
        id: string;
        key: string;
      }[] = [];
      for (const factiii of postFactiiiCounts) {
        const photo = factiii.postFactiiis[0].photo;
        if (photo) {
          const base64 = await uriToBase64(photo.uri);
          const result = await uploadFromBase64({
            base64
          });
          if (result) {
            factiiiUploads.push({
              factiiiId: factiii.factiiiId,
              id: result.id,
              key: result.key
            });
          }
        }
      }

      // Attach the uploads to the factiii. View width/height stay null —
      // private base64 path doesn't decode dimensions, but key is real and
      // resolves to a local blob URL on render.
      const postFactiiiCountsWithUploads: FactiiiTag[] = postFactiiiCounts.map(factiii_0 => {
        const factiiiUpload = factiiiUploads.find(upload => upload.factiiiId === factiii_0.factiiiId);
        return {
          ...factiii_0,
          postFactiiis: [{
            ...factiii_0.postFactiiis[0],
            uploads: factiiiUpload ? [{
              id: factiiiUpload.id,
              display: {
                key: factiiiUpload.key,
                width: null,
                height: null,
                contentType: 'image/jpeg'
              }
            }] : factiii_0.postFactiiis[0].uploads
          }, ...factiii_0.postFactiiis.slice(1)]
        };
      });
      const {
        addFactiiis,
        removeFactiiiIds
      } = remappedAddFactiiis(postFactiiiCountsWithUploads, queryKey);
      const success = await processPrivateFactiiis(props.post.id, addFactiiis, removeFactiiiIds);
      if (success) {
        props.refreshPost(props.post.id);
      }
    } else {
      // Check for moderation-required factiiis and show confirmation
      const moderationRequiredFactiiis = postFactiiiCounts.filter(factiii_1 => factiii_1.isNewFactiii && factiii_1.requirements.includes(FactiiiRequirement.POSTFACTIII_MODERATION_REQUIRED)).map(factiii_2 => factiii_2.name);
      if (moderationRequiredFactiiis.length > 0) {
        toast(`This post contains factiiis (${moderationRequiredFactiiis.join(', ')}) that require moderation approval. These factiiis will be reviewed by the factiii's mods before it appears on your post.`, 'info');
      }

      // Upload factiii photos. Each photo is factiii evidence — pass the
      // factiii context so the compose helper bakes the source factiii's
      // avatar into the VIEW variant. See `.specs/watermarking.md`.
      // TODO spinner for when uploading
      const factiiiUploads_0: {
        factiiiId: number;
        upload: Upload;
      }[] = [];
      for (const factiii_3 of postFactiiiCounts) {
        const photo_0 = factiii_3.postFactiiis[0].photo;
        if (photo_0) {
          const dimensions = await getDimensionsFromUri(photo_0.uri);
          const result_0 = await uploadOne(photo_0.uri, undefined, dimensions ?? undefined, {
            factiii: {
              factiiiId: factiii_3.factiiiId,
              factiiiAvatarKey: factiii_3.avatarKey
            }
          });
          if (result_0) {
            factiiiUploads_0.push({
              factiiiId: factiii_3.factiiiId,
              upload: result_0
            });
          }
        }
      }
      // Attach the uploads to the factiii — use the real view returned by
      // uploadOne; no fabricated placeholder.
      const postFactiiiCountsWithUploads_0: FactiiiTag[] = postFactiiiCounts.map(factiii_4 => {
        const factiiiUpload_0 = factiiiUploads_0.find(upload_0 => upload_0.factiiiId === factiii_4.id);
        return {
          ...factiii_4,
          postFactiiis: [{
            ...factiii_4.postFactiiis[0],
            uploads: factiiiUpload_0 ? [{
              id: factiiiUpload_0.upload.id,
              display: factiiiUpload_0.upload.display
            }] : factiii_4.postFactiiis[0].uploads
          }, ...factiii_4.postFactiiis.slice(1)]
        };
      });
      const {
        addFactiiis: addFactiiis_0,
        removeFactiiiIds: removeFactiiiIds_0
      } = remappedAddFactiiis(postFactiiiCountsWithUploads_0, queryKey);
      const [, data] = await client.factiiis.tag.mutate({
        postId: props.post.id,
        addFactiiis: addFactiiis_0,
        removeFactiiiIds: removeFactiiiIds_0,
        addAwards: selectedAwards.map(a => ({
          awardId: a.id,
          anonymous: a.anonymous,
          quantity: a.count
        }))
      });
      if (data) {
        props.refreshPost(props.post.id);
      }
    }
    setButtonLoading(false);
    props.closeHandler();
  };
  const requirementsMet = checkRequirementsMet({
    content: props.post.content,
    postFactiiisCount: [],
    //Empty because the myPostFactiiiCounts is used for validation
    uploads: props.post.uploads ?? []
  }, myPostFactiiiCounts);
  const costBreakdown = props.post.isPrivate ? {
    tagEditFee: 0,
    botCost: 0,
    awardCost: 0,
    total: 0
  } : calculateEditTronCostBreakdown(myPostFactiiiCounts, props.post.postFactiiisCount, selectedAwards.map(a_0 => ({
    cost: a_0.cost,
    count: a_0.count
  })));
  // Award gives are folded into the cost estimate (balance-only, like the tag
  // fee), so insufficientTrons already accounts for them.
  const {
    insufficientTrons,
    poolCoversRemainder
  } = useTronAffordability(costBreakdown);

  // Value-equality vs the server snapshot (edit-form-pattern spec). Reverting
  // every change re-disables Tag and hides Reset. superjson handles the Dates.
  const isEdited = superjson.stringify(myPostFactiiiCounts) !== superjson.stringify(initialData) || selectedAwards.length > 0;
  const handleReset = () => {
    setMyPostFactiiiCounts(initialData);
    setSelectedAwards([]);
  };

  // What's keeping Tag disabled — surfaced so it isn't a mystery which factiii
  // is incomplete (Tag needs EVERY selected factiii's requirements met).
  const blockingReqs = requirementsMet.flatMap(r => r.results.filter(x => !x.completed).map(x_0 => ({
    name: r.tag.name,
    desc: x_0.description
  })));
  return <Modal closeHandler={props.closeHandler}>
      {/* Header — Factiiis & Awards title */}
      <ModalHeader closeHandler={props.closeHandler}>
        <h2 data-id="factiii-modal" className="text-lg font-semibold text-textPrimary">
          Factiiis & Awards
        </h2>
      </ModalHeader>

      {/* Content */}
      <ModalContent fillHeight>
        {/* Collapsible post preview — collapsed by default to save space; a
            compact one-line summary expands to the full post head + body. */}
        <div className="mb-3 cursor-pointer rounded-md border border-ds-border-subtle px-3 py-2" onClick={() => setShowPostBody(x_1 => !x_1)}>
          <div className="flex items-center justify-between gap-2">
            {showPostBody ? <div className="min-w-0 flex-1">
                <PostItemHead post={props.post} nsfw={props.nsfw} politics={props.politics} hideMenu />
              </div> : <span className="min-w-0 flex-1 truncate text-xs text-textSecondary">
                Tagging @{props.post.user?.username ?? 'this'}&apos;s post — tap
                to preview
              </span>}
            <div className="shrink-0 text-textSecondary">
              {showPostBody ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </div>
          </div>
          {showPostBody && <div className="mt-2" onClick={e => e.stopPropagation()}>
              <PostItemBody post={props.post} politics={props.politics} nsfw={props.nsfw} disableLink isFullLength={false} troll={props.troll} isInView ignorePostFactiiiIds={myPostFactiiiCounts.map(f => f.id)} minified />
              {!preferenceState.allowPoliticalContent && props.politics && <div className="my-4 rounded-lg bg-destructive/10 py-2 text-destructive">
                  Content Marked Political&nbsp;
                  <Link href={'/settings/preferences'} className="text-primary underline" onClick={e_0 => e_0.stopPropagation()}>
                    click here
                  </Link>
                  &nbsp;to update your settings
                </div>}
            </div>}
        </div>

        <NewPostFactiii requirementsMet={requirementsMet} postId={props.post.id} myPostFactiiiCounts={myPostFactiiiCounts} setMyPostFactiiiCounts={setMyPostFactiiiCounts} setNsfw={props.setNsfw} setPolitics={props.setPolitics} queryKey={queryKey} isPrivate={props.post.isPrivate} isOpened postFactiiisCount={props.post.postFactiiisCount} postAuthorName={props.post.user?.username} selectedAwards={selectedAwards} setSelectedAwards={setSelectedAwards} setInitialData={setInitialData} costInFooter />
      </ModalContent>

      <ModalFooter>
        <div className="flex w-full flex-col gap-2">
          {/* Cost + balance summary — moved out of the scrolling body so it
              doesn't eat vertical space. Only shows past the free limit. */}
          {costBreakdown.total > 0 && <div className={`flex items-center justify-between gap-2 text-xs ${insufficientTrons ? 'text-destructive' : 'text-textSecondary'}`}>
              <span>
                Cost estimate:{' '}
                <strong>{costBreakdown.total.toLocaleString()}</strong> trons
                {/* Spell out the parts so the total clearly adds up — tag fee
                    (first 10 factiiis free), bot replies, and award gives. */}
                {[costBreakdown.tagEditFee && `tags ${costBreakdown.tagEditFee}`, costBreakdown.botCost && `bots ${costBreakdown.botCost}`, costBreakdown.awardCost && `awards ${costBreakdown.awardCost}`].filter(Boolean).length > 1 && <span className="text-textSecondary/70">
                    {' '}
                    (
                    {[costBreakdown.tagEditFee && `tags ${costBreakdown.tagEditFee.toLocaleString()}`, costBreakdown.botCost && `bots ${costBreakdown.botCost.toLocaleString()}`, costBreakdown.awardCost && `awards ${costBreakdown.awardCost.toLocaleString()}`].filter(Boolean).join(' + ')}
                    )
                  </span>}
              </span>
              <span className="shrink-0 text-right">
                Balance:{' '}
                <strong>{currentUser.tronsBalance.toLocaleString()}</strong>
                {insufficientTrons && ' — insufficient'}
                {poolCoversRemainder && <span className="text-primary">
                    {' '}
                    — community pool covers remainder
                  </span>}
              </span>
            </div>}
          {/* One-line nudge — the per-factiii specifics live on each card. */}
          {isEdited && blockingReqs.length > 0 && <span className="text-xs text-textSecondary">
              {blockingReqs.length} requirement
              {blockingReqs.length > 1 ? 's' : ''} still needed on the
              highlighted factiiis
            </span>}
          <div className="flex w-full items-center justify-between">
            <DefaultFactiiiButtons myPostFactiiiCounts={myPostFactiiiCounts} setMyPostFactiiiCounts={setMyPostFactiiiCounts} setNsfw={props.setNsfw} setPolitics={props.setPolitics} requirementsMet={requirementsMet} />
            <div className="flex items-center gap-2">
              {/* Reset appears only when the working set differs from the
                  server snapshot; it reverts data back to initialData. */}
              {isEdited && <Button text="Reset" variant="SECONDARY" onClick={handleReset} disabled={buttonLoading} />}
              <Button text="Tag" onClick={() => submitHandler(myPostFactiiiCounts)} loading={buttonLoading} disabled={buttonLoading || !isEdited || insufficientTrons || blockingReqs.length > 0} />
            </div>
          </div>
        </div>
      </ModalFooter>
    </Modal>;
};
export default FactiiiModal;